import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Box, Container, Typography, CardMedia, Button, Chip, Paper,
    CircularProgress, Alert, Stack, Grid, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, IconButton
} from '@mui/material';

// Icons
import {
    Hotel as HotelIcon,
    Person as PersonIcon,
    ArrowBack as ArrowBackIcon,
    LocationOn as LocationIcon,
    Close as CloseIcon,
    ImageNotSupported as NoImageIcon
} from '@mui/icons-material';

// Firebase
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const MotionBox = motion(Box);

export default function Hoteldetails() {
    const { propertyId } = useParams();
    const navigate = useNavigate();

    const [property, setProperty] = useState(null);
    const [allRooms, setAllRooms] = useState([]);
    const [propertyImages, setPropertyImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state for availability check
    const [openModal, setOpenModal] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState('');
    const [checkIn, setCheckIn] = useState('');
    const [checkOut, setCheckOut] = useState('');

    useEffect(() => {
        // Set default dates: check-in (today) & check-out (tomorrow)
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);

        setCheckIn(today.toISOString().split('T')[0]);
        setCheckOut(tomorrow.toISOString().split('T')[0]);

        if (!propertyId) {
            setError('No property ID provided.');
            setLoading(false);
            return;
        }

        const fetchAllData = async () => {
            try {
                setLoading(true);

                // 1. Fetch Property Details
                let propData = null;
                const propRef = doc(db, 'properties', propertyId);
                const propSnap = await getDoc(propRef);

                if (propSnap.exists()) {
                    propData = { id: propSnap.id, ...propSnap.data() };
                } else {
                    const q = query(collection(db, 'properties'), where('propertyId', '==', propertyId));
                    const qSnap = await getDocs(q);
                    if (!qSnap.empty) {
                        propData = { id: qSnap.docs[0].id, ...qSnap.docs[0].data() };
                    }
                }

                if (!propData) {
                    setError(`Property with ID "${propertyId}" not found.`);
                    setLoading(false);
                    return;
                }
                setProperty(propData);

                // 2. Fetch Property Images from `property_images` collection using propertyId
                const imgQuery = query(collection(db, 'property_images'), where('propertyId', '==', propertyId));
                const imgSnap = await getDocs(imgQuery);
                const fetchedPropImages = imgSnap.docs.map(doc => doc.data().imageUrl).filter(Boolean);
                setPropertyImages(fetchedPropImages);

                // 3. Fetch Rooms matching propertyId
                const roomsQuery = query(collection(db, 'rooms'), where('propertyId', '==', propertyId));
                const roomsSnap = await getDocs(roomsQuery);
                const rawRooms = roomsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

                // 4. Fetch Rates matching propertyId from `roomrates` collection
                const ratesQuery = query(collection(db, 'roomrates'), where('propertyId', '==', propertyId));
                const ratesSnap = await getDocs(ratesQuery);
                
                // Map rates by roomId for O(1) lookup
                const ratesMap = {};
                ratesSnap.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.roomId) {
                        // Store the rate (or rate plan details) associated with the roomId
                        ratesMap[data.roomId] = data;
                    }
                });

                // Combine room data with rates from `roomrates`
                const updatedRooms = rawRooms.map(room => {
                    const rateDoc = ratesMap[room.id] || ratesMap[room.roomId];
                    const matchedRate = rateDoc ? rateDoc.rate : (room.pricing?.basePrice || 0);
                    const planName = rateDoc ? rateDoc.planName : 'Standard';

                    return {
                        ...room,
                        rate: matchedRate,
                        planName: planName
                    };
                });

                setAllRooms(updatedRooms);
                if (updatedRooms.length > 0) {
                    setSelectedRoomId(updatedRooms[0].id);
                }

            } catch (err) {
                console.error('Error fetching property details:', err);
                setError('Failed to load property details.');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [propertyId]);

    const handleOpenModal = (roomId = null) => {
        if (roomId) setSelectedRoomId(roomId);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleProceed = () => {
        const propNameEncoded = encodeURIComponent(property?.hotelname || '');
        const targetRoomId = selectedRoomId || (allRooms[0]?.id || '');

        const queryString = `?propertyId=${propertyId}&propertyName=${propNameEncoded}&checkIn=${checkIn}&checkOut=${checkOut}&roomTypeId=${targetRoomId}`;
        navigate(`/check-availability${queryString}`);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#F8F9FA' }}>
                <CircularProgress sx={{ color: '#B8860B' }} size={60} thickness={3} />
                <Typography variant="body1" sx={{ mt: 3, fontWeight: 700, color: '#4A5568', letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.85rem' }}>
                    Loading Luxury Experience...
                </Typography>
            </Box>
        );
    }

    if (error || !property) {
        return (
            <Box sx={{ bgcolor: '#F8F9FA', minHeight: '100vh', py: 10 }}>
                <Container maxWidth="md">
                    <Paper elevation={0} sx={{ p: 4, borderRadius: '24px', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                        <Alert severity="error" sx={{ borderRadius: '16px', fontWeight: 600 }}>
                            {error || 'Unable to load property details.'}
                        </Alert>
                        <Button
                            variant="contained"
                            startIcon={<ArrowBackIcon />}
                            sx={{ mt: 3, bgcolor: '#1A2E2B', color: '#FFFFFF', borderRadius: '12px', fontWeight: 800, textTransform: 'none', px: 3, py: 1.2, '&:hover': { bgcolor: '#2C4A45' } }}
                            onClick={() => navigate('/')}
                        >
                            Back to Hotels
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    // Dynamic gallery resolution: prioritize fetched property_images > property.buildingImage > fallback assets
    const defaultGallery = [
        'https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=800&q=80',
        'https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=800&q=80'
    ];

    const gallery = propertyImages.length > 0 
        ? propertyImages 
        : (property.buildingImage ? [property.buildingImage, ...defaultGallery.slice(1)] : defaultGallery);

    return (
        <Box sx={{ bgcolor: '#F4F6F8', color: '#1A202C', minHeight: '100vh', pb: 12 }}>
            <Grid className="container" sx={{ pt: 4 }}>
                <MotionBox initial={{ opacity: 0, y: -15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} mb={4}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                        sx={{
                            bgcolor: '#E2E8F0',
                            color: '#2D3748',
                            fontWeight: 700,
                            borderRadius: '10px',
                            px: 2, py: 0.8, mb: 2,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#CBD5E0' }
                        }}
                    >
                        Back to Properties
                    </Button>

                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" mb={2} alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={2}>
                        <Box>
                            <Typography variant="h3" fontWeight={900} sx={{ letterSpacing: '-0.5px', color: '#0F172A', fontSize: { xs: '2rem', md: '2.6rem' } }}>
                                {property.hotelname}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1} color="#475569" mt={1}>
                                <LocationIcon sx={{ fontSize: 20, color: '#B8860B' }} />
                                <Typography variant="body1" fontWeight={600}>
                                    {property.city}{property.state ? `, ${property.state}` : ''}{property.country ? `, ${property.country}` : ''}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </MotionBox>

                {/* Dynamic Hero Photo Grid from property_images collection */}
                <Grid className="row" spacing={2} mb={5} style={{marginTop:5}}>
                    <Grid className="col-sm-8" item xs={12} md={8}>
                        <Box sx={{
                            borderRadius: '24px',
                            overflow: 'hidden',
                            height: { xs: '280px', md: '420px' },
                            position: 'relative',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                            border: '1px solid #E2E8F0',
                            bgcolor: '#E2E8F0'
                        }}>
                            <CardMedia 
                                component="img" 
                                image={property.buildingImage} 
                                alt={property.hotelname}
                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                        </Box>
                    </Grid>
                    <Grid className="col-sm-4" item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Stack spacing={2} height="100%">
                            <Box sx={{ borderRadius: '24px', overflow: 'hidden', height: '202px', border: '1px solid #E2E8F0', boxShadow: '0 8px 20px rgba(0,0,0,0.06)', bgcolor: '#E2E8F0' }}>
                                <CardMedia component="img" image={gallery[1] || gallery[0]} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                            <Box sx={{ borderRadius: '24px', overflow: 'hidden', height: '202px', border: '1px solid #E2E8F0', boxShadow: '0 8px 20px rgba(0,0,0,0.06)', bgcolor: '#E2E8F0' }}>
                                <CardMedia component="img" image={gallery[2] || gallery[0]} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

              
                <Grid className="row mt-5" spacing={3.5}>
                    {allRooms.map((room) => {
                        const finalPrice = room.rate || 0;

                        return (
                            <Grid className="col-sm-6 mb-3" item xs={12} md={6} key={room.id}>
                                <MotionBox
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4 }}
                                    sx={{
                                        borderRadius: '24px',
                                        overflow: 'hidden',
                                        boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                                        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            borderColor: '#D4AF37',
                                            boxShadow: '0 20px 40px rgba(212, 175, 55, 0.12)',
                                            transform: 'translateY(-4px)'
                                        }
                                    }}
                                >
                                    <Grid className="row" sx={{ minHeight: { sm: 220 } }}>
                                        <Grid className="col-sm-4"
                                            item
                                            xs={12}
                                            sm={5}
                                            sx={{ position: 'relative', overflow: 'hidden', minHeight: { xs: 180, sm: 'auto' }, bgcolor: '#1A2E2B' }}
                                        >
                                            {room.imageUrl ? (
                                                <MotionBox
                                                    component="img"
                                                    src={room.imageUrl}
                                                    alt={room.roomTypeName || 'Room Image'}
                                                    whileHover={{ scale: 1.06 }}
                                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#A1B2AF' }}>
                                                    <NoImageIcon sx={{ fontSize: 40 }} />
                                                </Box>
                                            )}
                                        </Grid>

                                        <Grid className="col-sm-8"
                                            item
                                            xs={12}
                                            sm={7}
                                            sx={{
                                                p: { xs: 2.5, md: 3 },
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'space-between'
                                            }}
                                        >
                                            <Box>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                                    <Typography
                                                        variant="h6"
                                                        fontWeight={800}
                                                        color="#FFFFFF"
                                                        sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' }, letterSpacing: '-0.3px' }}
                                                    >
                                                        {room.roomTypeName || room.name || 'Deluxe Room'}
                                                    </Typography>
                                                </Stack>

                                                {room.planName && (
                                                    <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 700, display: 'block', mb: 1 }}>
                                                        Plan: {room.planName}
                                                    </Typography>
                                                )}

                                                <Typography
                                                    variant="body2"
                                                    color="#A1B2AF"
                                                    mb={2}
                                                    sx={{
                                                        lineHeight: 1.5,
                                                        fontSize: '0.85rem',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    {room.description || 'Spacious ambient room styled for luxury relaxation with modern amenities.'}
                                                </Typography>

                                                <Stack style={{marginTop:8, marginBottom:8}} direction="row" spacing={1} flexWrap="wrap" gap={1} mb={2}>
                                                    <Chip
                                                        icon={<HotelIcon sx={{ color: '#D4AF37 !important', fontSize: 16 }} />}
                                                        label="King Bed"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(212, 175, 55, 0.1)',
                                                            color: '#0e0606',
                                                            fontWeight: 700,
                                                            border: '1px solid rgba(212, 175, 55, 0.25)',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                    <Chip
                                                        icon={<PersonIcon sx={{ color: '#D4AF37 !important', fontSize: 16 }} />}
                                                        label={`Up to ${room.capacity?.adults || room.capacity || 2} Guests`}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: 'rgba(212, 175, 55, 0.1)',
                                                            color: '#0e0606',
                                                            fontWeight: 700,
                                                            border: '1px solid rgba(212, 175, 55, 0.25)',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    />
                                                </Stack>
                                            </Box>

                                            <Box sx={{ pt: 2, borderTop: '1px solid #484949' }}>
                                                <Grid className="row" alignItems="center">
                                                    {/* <Grid className="col-sm-6" item xs={6}>
                                                        <Stack direction="row" alignItems="baseline" spacing={0.5}>
                                                            <Typography variant="h5" fontWeight={900} color="#D4AF37" sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                                                                ₹{finalPrice.toLocaleString()}
                                                            </Typography>
                                                            <Typography variant="caption" color="#A1B2AF" fontWeight={600}>
                                                                / night
                                                            </Typography>
                                                        </Stack>
                                                    </Grid> */}

                                                    <Grid className="col-sm-6" item xs={6} sx={{ textAlign: 'right' }}>
                                                        <Button
                                                            fullWidth
                                                            variant="contained"
                                                            onClick={() => handleOpenModal(room.id)}
                                                            sx={{
                                                                bgcolor: '#D4AF37',
                                                                color: '#0B1B19',
                                                                fontWeight: 800,
                                                                borderRadius: '12px',
                                                                py: 1,
                                                                px: 1.5,
                                                                fontSize: '0.8rem',
                                                                textTransform: 'none',
                                                                boxShadow: '0 4px 16px rgba(212, 175, 55, 0.25)',
                                                                transition: 'all 0.25s ease',
                                                                '&:hover': {
                                                                    bgcolor: '#B8952B',
                                                                    boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                                                                    transform: 'scale(1.02)'
                                                                }
                                                            }}
                                                        >
                                                            Select & Check
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </MotionBox>
                            </Grid>
                        );
                    })}
                </Grid>
            </Grid>

            {/* Check Availability Popup Dialog */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: '#FFFFFF',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        color: '#0F172A',
                        maxWidth: '480px',
                        width: '100%',
                        p: 1
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                    <Typography variant="h6" fontWeight={800} color="#0F172A">
                        Check Availability
                    </Typography>
                    <IconButton onClick={handleCloseModal} size="small" sx={{ color: '#64748B' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={3}>
                        <TextField
                            select
                            fullWidth
                            label="Room Type"
                            value={selectedRoomId}
                            onChange={(e) => setSelectedRoomId(e.target.value)}
                            variant="outlined"
                        >
                            {allRooms.map((rm) => (
                                <MenuItem key={rm.id} value={rm.id}>
                                    {rm.roomTypeName || rm.name || 'Standard Room'} - ₹{(rm.rate || 0).toLocaleString()}/night
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            fullWidth
                            type="date"
                            label="Check-In Date"
                            value={checkIn}
                            onChange={(e) => setCheckIn(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            fullWidth
                            type="date"
                            label="Check-Out Date"
                            value={checkOut}
                            onChange={(e) => setCheckOut(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ p: 2, pt: 1 }}>
                    <Button onClick={handleCloseModal} sx={{ color: '#64748B', fontWeight: 700, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleProceed}
                        sx={{
                            bgcolor: '#1A2E2B',
                            color: '#FFFFFF',
                            fontWeight: 800,
                            borderRadius: '12px',
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#2C4A45' }
                        }}
                    >
                        Proceed
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}